/**
 * Per-browser memory of the user's last-used application and venue-per-app.
 * A convenience default only — never overrides an explicit address
 * (specs/016-global-nav-venue-sync/data-model.md).
 */

const STORAGE_KEY = 'manager:lastUsed';
const LEGACY_KEY = 'manager:lastVenueByApp';

export type LastUsed = {
  v: 1;
  lastApp?: string;
  lastVenueByApp: Record<string, string>;
};

const EMPTY: LastUsed = { v: 1, lastVenueByApp: {} };

const RETIRED_APPS = new Set(['trackd']);

const isVenueMap = (value: unknown): value is Record<string, string> =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.values(value).every((entry) => typeof entry === 'string');

/**
 * A browser that remembers a retired application must fall through to the default-app rule
 * rather than address a product that no longer exists (FR-010).
 */
const withoutRetiredApps = (stored: LastUsed): LastUsed => {
  const lastVenueByApp = Object.fromEntries(
    Object.entries(stored.lastVenueByApp).filter(([app]) => !RETIRED_APPS.has(app)),
  );
  const lastApp = stored.lastApp && RETIRED_APPS.has(stored.lastApp) ? undefined : stored.lastApp;

  return lastApp ? { v: 1, lastApp, lastVenueByApp } : { v: 1, lastVenueByApp };
};

export function readLastUsed(): LastUsed {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (
        !!parsed &&
        typeof parsed === 'object' &&
        (parsed as LastUsed).v === 1 &&
        isVenueMap((parsed as LastUsed).lastVenueByApp)
      ) {
        return withoutRetiredApps(parsed as LastUsed);
      }
      // Unknown shape under our key — reset rather than guess.
      return { ...EMPTY, lastVenueByApp: {} };
    }

    // Migrate the legacy bare venue map in place.
    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    if (legacyRaw) {
      const legacy: unknown = JSON.parse(legacyRaw);
      if (isVenueMap(legacy)) {
        const migrated = withoutRetiredApps({ v: 1, lastVenueByApp: legacy });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
        localStorage.removeItem(LEGACY_KEY);
        return migrated;
      }
      localStorage.removeItem(LEGACY_KEY);
    }
  } catch {
    // Corrupt storage or storage unavailable — behave as if empty.
  }

  return { ...EMPTY, lastVenueByApp: {} };
}

export function writeLastUsed(app: string, venueId: string): void {
  try {
    const current = readLastUsed();
    const next: LastUsed = {
      v: 1,
      lastApp: app,
      lastVenueByApp: { ...current.lastVenueByApp, [app]: venueId },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable — memory is a convenience, never an error.
  }
}
